import React from 'react';

import type { DividerProps, DividerVariant } from './Divider.types';

/**
 * Maps variant to border color class.
 */
const VARIANT_CLASSES: Record<DividerVariant, string> = {
  subtle: 'border-stroke-subtle',
  default: 'border-stroke-default',
  strong: 'border-stroke-default',
};

/**
 * Label styling matching the kicker pattern from the design system.
 */
const LABEL_CLASSES =
  'text-label-xs font-semibold uppercase tracking-wider text-neutral-500';

/**
 * Divider primitive for consistent visual separators across the app.
 *
 * Provides horizontal and vertical line separators with optional labels,
 * following the Dō Sakura design system tokens.
 *
 * @example
 * ```tsx
 * // Simple horizontal divider
 * <Divider />
 *
 * // Divider with centered label
 * <Divider label="Section Title" labelPosition="center" />
 *
 * // Strong vertical divider
 * <Divider orientation="vertical" variant="strong" />
 *
 * // Subtle divider with left-aligned label
 * <Divider label="Options" labelPosition="left" variant="subtle" />
 * ```
 */
export function Divider({
  label,
  labelPosition = 'left',
  variant = 'default',
  orientation = 'horizontal',
  className = '',
}: DividerProps): React.JSX.Element {
  const variantClass = VARIANT_CLASSES[variant];

  // Vertical dividers don't support labels
  if (orientation === 'vertical') {
    return (
      <div
        role="separator"
        aria-orientation="vertical"
        className={`h-full border-l ${variantClass} ${className}`.trim()}
      />
    );
  }

  // Horizontal divider without label
  if (!label) {
    return (
      <hr
        role="separator"
        aria-orientation="horizontal"
        className={`w-full border-t ${variantClass} ${className}`.trim()}
      />
    );
  }

  // Horizontal divider with label
  return (
    <div
      role="separator"
      aria-orientation="horizontal"
      className={`flex items-center gap-3 ${className}`.trim()}
    >
      {labelPosition === 'left' && (
        <span className={LABEL_CLASSES}>{label}</span>
      )}

      {labelPosition === 'center' && (
        <div className={`flex-1 border-t ${variantClass}`} />
      )}

      {labelPosition === 'center' && (
        <span className={LABEL_CLASSES}>{label}</span>
      )}

      {labelPosition === 'right' && (
        <div className={`flex-1 border-t ${variantClass}`} />
      )}

      <div className={`flex-1 border-t ${variantClass}`} />

      {labelPosition === 'left' && null}
      {labelPosition === 'right' && (
        <span className={LABEL_CLASSES}>{label}</span>
      )}
    </div>
  );
}

export default Divider;
